<template>
  <div class="musubi-page musubi-page--admin-home">
    <header class="musubi-page__head">
      <h1>Admin</h1>
      <p class="text-muted">
        Configuration and operator surfaces — email infrastructure, brand &amp; content,
        billing, the acquisition engine, domains and governance. Everything here is gated:
        you only see what your role can reach.
      </p>
    </header>

    <!-- EMAIL-SAFETY — standing operator banner while the platform test-mode
         send gate is on. Operators only; a tenant never sees this. -->
    <div v-if="testMode.enabled" class="admin-home__testmode">
      <CygnusIcon name="shield" class="admin-home__testmode-ic" />
      <div class="admin-home__testmode-copy">
        <div class="admin-home__testmode-title">
          TEST MODE ON - email only reaches: {{ testMode.domains.join(', ') }}
        </div>
        <div class="admin-home__testmode-sub">
          Every other recipient is blocked before delivery and marked failed. Clear the
          allowed domains on Issue routing to resume normal sending.
        </div>
      </div>
      <router-link to="/platform/ticket-routing" class="admin-home__testmode-cta">
        Manage <CygnusIcon name="arrow-right" />
      </router-link>
    </div>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <div v-if="loading" class="admin-home__loading text-muted small">
      <CygnusIcon name="spinner" :spin="true" class="me-1" />Loading admin area…
    </div>

    <p v-else-if="!groups.length" class="text-muted">
      You don't have access to any admin surfaces.
    </p>

    <section
      v-for="group in groups"
      :key="group.key"
      class="admin-home__group"
      :class="{ 'is-gated': group.locked }"
    >
      <h2 class="admin-home__group-title">
        {{ group.title }}
        <span v-if="group.gate" class="admin-home__ent-chip">
          <CygnusIcon name="award" /> Included in Enterprise
        </span>
      </h2>

      <!-- Locked cluster: honest Enterprise banner + Compare plans (not "Upgrade"). -->
      <div v-if="group.locked" class="admin-home__ent-banner">
        <div class="admin-home__ent-ic"><CygnusIcon name="award" /></div>
        <div class="admin-home__ent-copy">
          <div class="admin-home__ent-title">Corpus-wide data views are an Enterprise feature</div>
          <div class="admin-home__ent-sub">
            Browse and filter the raw prospect corpus, manage taxonomies, and share saved
            corpus views with your team. Your current plan works from per-pipeline matches —
            these open up the whole corpus.
          </div>
        </div>
        <router-link to="/admin/usage-plans" class="admin-home__ent-cta">
          Compare plans <CygnusIcon name="arrow-right" />
        </router-link>
      </div>

      <div class="admin-home__grid">
        <template v-for="tile in group.tiles" :key="tile.title">
          <!-- Locked tile — non-navigating placeholder, no fabricated counts. -->
          <div v-if="tile.locked" class="admin-home__tile is-locked">
            <CygnusIcon :name="tile.icon || 'question'" class="admin-home__tile-icon" />
            <span class="admin-home__tile-title">{{ tile.title }}</span>
            <span class="admin-home__tile-lock"><CygnusIcon name="lock" /> Enterprise</span>
          </div>

          <router-link
            v-else
            :to="tile.route"
            class="admin-home__tile"
          >
            <CygnusIcon :name="tile.icon || 'question'" class="admin-home__tile-icon" />
            <span class="admin-home__tile-title">{{ tile.title }}</span>
            <CygnusIcon name="arrow-right" class="admin-home__tile-go" />
          </router-link>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * AdminHome — the /admin landing tile grid (the launcher hub). Fetches the same
 * permission-filtered tile data as the rail-native Admin group (GET
 * /api/v1/shell/admin/settings-tiles) and renders it as grouped cards. One
 * backend registration (AdminTilesApiController) drives both surfaces, so the
 * hub never drifts from the rail. Feature-gated groups (e.g. Data views on
 * `data_views`) arrive with `locked: true` for operators on a non-Enterprise
 * plan — rendered here as the honest "Included in Enterprise" treatment.
 */
import useApi from '@apexl/icarus-core/src/services/api';

export default {
  name: 'AdminHome',
  props: {
    apiBase: { type: String, default: '/api/v1' },
  },
  data() {
    return {
      groups: [],
      testMode: { enabled: false, domains: [] },
      loading: true,
      error: '',
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await useApi().get(`${this.apiBase}/shell/admin/settings-tiles`);
        this.groups = res?.groups || [];
        this.testMode = res?.email_test_mode || { enabled: false, domains: [] };
      } catch (e) {
        this.error = 'Failed to load the admin area.';
        this.groups = [];
        this.testMode = { enabled: false, domains: [] };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.musubi-page--admin-home {
  .admin-home__loading { padding: 1rem 0; }

  /* EMAIL-SAFETY — standing test-mode banner (operator only). */
  .admin-home__testmode {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    border: 1px solid var(--m-status-warning, #D97706);
    background: #FEF6E7;
    border-radius: 12px;
    padding: 0.8rem 1.05rem;
    margin-bottom: 1rem;
  }

  .admin-home__testmode-ic {
    flex: 0 0 auto;
    color: var(--m-status-warning, #D97706);
    font-size: 1.15rem;
  }

  .admin-home__testmode-copy { flex: 1 1 auto; }

  .admin-home__testmode-title {
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--m-text-primary);
    letter-spacing: 0.01em;
  }

  .admin-home__testmode-sub {
    font-size: 0.78rem;
    color: var(--m-text-secondary);
    margin-top: 2px;
  }

  .admin-home__testmode-cta {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #fff;
    background: var(--m-status-warning, #D97706);
    border-radius: 8px;
    padding: 0.5rem 0.9rem;
    text-decoration: none;
    transition: background 0.15s;
    &:hover { background: #B45309; }
  }

  .admin-home__group {
    margin-top: 2rem;
    &:first-of-type { margin-top: 1.25rem; }
  }

  .admin-home__group-title {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--theme-text-muted, #6c757d);
    margin-bottom: 0.75rem;
  }

  // ── Enterprise (data_views) locked treatment — violet plan-gating accent ──
  .admin-home__ent-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    color: var(--m-violet, #7C3AED);
    background: var(--m-violet-soft, #F3EEFE);
    border: 1px solid var(--m-violet-line, #E0D2FB);
    border-radius: 999px;
    padding: 2px 9px;
    text-transform: none;
  }

  .admin-home__ent-banner {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    background: linear-gradient(135deg, var(--m-violet-soft, #F3EEFE), #FBFAFF);
    border: 1px solid var(--m-violet-line, #E0D2FB);
    border-radius: 12px;
    padding: 0.85rem 1.1rem;
    margin-bottom: 1rem;
  }

  .admin-home__ent-ic {
    flex: 0 0 auto;
    width: 36px; height: 36px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 10px;
    background: #fff;
    border: 1px solid var(--m-violet-line, #E0D2FB);
    color: var(--m-violet, #7C3AED);
    font-size: 1rem;
  }

  .admin-home__ent-copy { flex: 1 1 auto; min-width: 0; }
  .admin-home__ent-title { font-size: 0.9rem; font-weight: 700; color: var(--theme-text-primary, #1A1C23); }
  .admin-home__ent-sub { font-size: 0.76rem; color: var(--theme-text-secondary, #475569); margin-top: 2px; }

  .admin-home__ent-cta {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #fff;
    background: var(--m-violet, #7C3AED);
    border-radius: 8px;
    padding: 0.5rem 0.9rem;
    text-decoration: none;
    transition: background 0.15s;
    &:hover { background: #6D28D9; }
  }

  .admin-home__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 0.75rem;
  }

  .admin-home__tile {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border: 1px solid var(--theme-border, rgba(0, 0, 0, 0.1));
    border-radius: 10px;
    background: var(--theme-bg-card, #fff);
    color: var(--theme-text, inherit);
    text-decoration: none;
    transition: border-color 0.12s, box-shadow 0.12s, transform 0.12s;

    &:hover {
      border-color: var(--theme-accent-primary, #0d6efd);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }
  }

  .admin-home__tile-icon {
    font-size: 1.1rem;
    width: 1.5rem;
    text-align: center;
    color: var(--theme-accent-primary, #0d6efd);
    flex: 0 0 auto;
  }

  .admin-home__tile-title {
    font-weight: 600;
    font-size: 0.95rem;
    flex: 1 1 auto;
  }

  .admin-home__tile-go {
    opacity: 0.35;
    font-size: 0.85rem;
    flex: 0 0 auto;
  }

  // Locked (Enterprise) tile — dashed violet, muted, non-navigating.
  .admin-home__tile.is-locked {
    background: #FCFBFE;
    border-style: dashed;
    border-color: var(--m-violet-line, #E0D2FB);
    cursor: default;

    .admin-home__tile-icon { color: var(--m-violet, #7C3AED); opacity: 0.85; }
    .admin-home__tile-title { color: var(--theme-text-secondary, #475569); }

    &:hover { transform: none; box-shadow: none; border-color: var(--m-violet, #7C3AED); }
  }

  .admin-home__tile-lock {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.58rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--m-violet, #7C3AED);
    background: var(--m-violet-soft, #F3EEFE);
    border: 1px solid var(--m-violet-line, #E0D2FB);
    border-radius: 999px;
    padding: 2px 8px;
  }
}
</style>
